import { useSelector, useDispatch } from "react-redux";
import {
  buildAlternativeCartItemPayload,
  convertImageNameToWeb,
} from "../../../../helpers/helperFunctions";
import { useEffect, useState } from "react";
import {
  getOrderItemRepresentation,
  setHoldDate,
  setSpecialInstructions,
} from "../../../../store/slices/OrderSlice/OrderSlice";
import { saveCartItem } from "../../../../store/slices/CartSlice/CartSlice";
import { ADDRESS_CLASSIFICATION } from "../../../../helpers/const";
import RemainingTime from "../../RemainTime";

export default function OrderConfirmation() {
  const dispatch = useDispatch();
  const {
    loadedOrderItems,
    selectedReduxRate,
    shippingInfo,
    holdDate,
    special_instructions,
  } = useSelector((state) => state.orders);
  const { customer, customerWarehouseCutoff, address_classification } =
    useSelector((state) => state.customer);
  const { cartItems } = useSelector((state) => state.cart);

  const [shippingCost, setShippingCost] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [tariffAmount, setTariffAmount] = useState(0);
  const [surchargeAmount, setSurchargeAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [swappingProductCode, setSwappingProductCode] = useState(null);

  const handleSwapToAlternative = async (item) => {
    const cartItem = cartItems.find(
      (ci) => ci.product_code === item.product?.product_code
    );
    if (!cartItem || !item.alternative) return;

    setSwappingProductCode(item.product.product_code);
    await dispatch(
      saveCartItem({
        cartItem: buildAlternativeCartItemPayload(cartItem, item.alternative),
      })
    );
    await dispatch(getOrderItemRepresentation());
    setSwappingProductCode(null);
  };

  // Helper to format a Date as YYYY-MM-DD in LOCAL time (avoids UTC offset shifting the date)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // The backend (System.get_cutoff_status, shared with the BOL print-time
  // cutoff enforcement) is the source of truth for whether cutoff has
  // passed and what the earliest valid hold date is — it knows the actual
  // warehouse cutoff and real EST clock, neither of which the browser can
  // reliably determine (browser timezone != EST, and the old hardcoded
  // ORDER_CUTOFF_HOUR didn't match any specific warehouse's cutoff).
  const getDefaultHoldDate = () => {
    if (customerWarehouseCutoff?.min_hold_date) {
      return customerWarehouseCutoff.min_hold_date;
    }
    // Fallback only for the rare case the cutoff endpoint hasn't returned
    // data yet (e.g. permission check failed, or request still in flight).
    return formatLocalDate(new Date());
  };

  // Helper function to get max date (7 days from today)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    return formatLocalDate(maxDate);
  };

  // Initialize hold date if not already set. Relies on customerWarehouseCutoff
  // already being loaded — App.js dispatches getCustomerWarehouseCutoff at
  // app mount, well before checkout reaches this step.
  useEffect(() => {
    if (!holdDate) {
      const defaultDate = getDefaultHoldDate();
      dispatch(setHoldDate(defaultDate));
    }
  }, [holdDate, dispatch]);

  // Handle date change
  const handleDateChange = (e) => {
    dispatch(setHoldDate(e.target.value));
  };

  // Handle special instructions change
  const handleSpecialInstructionsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 120) {
      dispatch(setSpecialInstructions(value));
    }
  };
  // Calculate total cost from items

  useEffect(() => {
    // Calculate shipping cost based on the new rate structure
    let calculatedShippingCost = 0;

    if (selectedReduxRate) {
      if (selectedReduxRate.type === "customer_pickup") {
        // Customer pickup is always free
        calculatedShippingCost = 0;
      } else if (
        selectedReduxRate.type === "all_rates" ||
        selectedReduxRate.type === "single_warehouse_rates"
      ) {
        // Handle alternative rates - calculate freight + fees, then apply free freight discount
        const warehouseDetails = Object.values(
          selectedReduxRate.data.combination_details || {},
        )[0];
        if (warehouseDetails) {
          const freightCost =
            warehouseDetails.FREIGHT_PRE_FEE ||
            warehouseDetails.PRICE ||
            warehouseDetails.price ||
            0;
          const fees = warehouseDetails.TOTAL_FEE_AMOUNT || 0;
          calculatedShippingCost = freightCost + fees;

          // Apply free freight discount if applicable
          if (shippingInfo?.free_freight) {
            // For alternative rates with free freight, subtract only the preferred freight (not fees)
            let preferredFreightTotal = 0;
            if (shippingInfo?.preferred_rate) {
              const convertRateToArray = (rateObject) => {
                if (!rateObject) return [];
                return Object.entries(rateObject).map(
                  ([location, carrierInfo]) => ({
                    location,
                    ...carrierInfo,
                  }),
                );
              };
              const preferredRates = convertRateToArray(
                shippingInfo.preferred_rate,
              );
              preferredFreightTotal = preferredRates.reduce((total, rate) => {
                const freight = rate.FREIGHT_PRE_FEE || rate.PRICE || 0;
                return total + freight; // Only subtract freight, keep fees
              }, 0);
            }

            // Subtract preferred freight from total freight, but keep all fees
            const totalFees = warehouseDetails.TOTAL_FEE_AMOUNT || 0;
            calculatedShippingCost = Math.max(
              0,
              freightCost - preferredFreightTotal + totalFees,
            );
          }
        }
      } else if (selectedReduxRate.type === "preferred_rate") {
        // Handle preferred rate - calculate freight + fees, then apply free freight discount
        if (
          selectedReduxRate.isMultiWarehouse &&
          Array.isArray(selectedReduxRate.data)
        ) {
          // Multi-warehouse preferred rate - sum freight + fees
          calculatedShippingCost = selectedReduxRate.data.reduce(
            (total, rate) => {
              const freight = rate.FREIGHT_PRE_FEE || rate.PRICE || 0;
              const fees = rate.TOTAL_FEE_AMOUNT || 0;
              return total + freight + fees;
            },
            0,
          );
        } else if (selectedReduxRate.data) {
          // Single rate - freight + fees
          const freightCost =
            selectedReduxRate.data.FREIGHT_PRE_FEE ||
            selectedReduxRate.data.PRICE ||
            0;
          const fees = selectedReduxRate.data.TOTAL_FEE_AMOUNT || 0;
          calculatedShippingCost = freightCost + fees;
        }

        // Apply free freight discount if applicable
        if (shippingInfo?.free_freight) {
          // For preferred rate with free freight, the cost is just the fees
          if (
            selectedReduxRate.isMultiWarehouse &&
            Array.isArray(selectedReduxRate.data)
          ) {
            calculatedShippingCost = selectedReduxRate.data.reduce(
              (total, rate) => total + (rate.TOTAL_FEE_AMOUNT || 0),
              0,
            );
          } else if (selectedReduxRate.data) {
            calculatedShippingCost =
              selectedReduxRate.data.TOTAL_FEE_AMOUNT || 0;
          }
        }
      } else {
        // Legacy rate handling
        if (
          selectedReduxRate.isMultiWarehouse &&
          Array.isArray(selectedReduxRate.data)
        ) {
          calculatedShippingCost = selectedReduxRate.data.reduce(
            (total, rate) => total + (rate.PRICE || 0),
            0,
          );
        } else if (selectedReduxRate.data?.PRICE !== undefined) {
          calculatedShippingCost = selectedReduxRate.data.PRICE || 0;
        } else if (selectedReduxRate.PRICE !== undefined) {
          calculatedShippingCost = selectedReduxRate.PRICE || 0;
        } else {
          calculatedShippingCost = Object.entries(selectedReduxRate)?.reduce(
            (prevValue, [key, value]) => {
              return prevValue + (value?.PRICE || 0);
            },
            0,
          );
        }
      }
    }

    setShippingCost(calculatedShippingCost);
    setSubtotal(
      loadedOrderItems.reduce(
        (sum, item) => sum + item.qty_ordered * item.item_price,
        0,
      ),
    );

    setTariffAmount(
      loadedOrderItems.reduce(
        (sum, item) => sum + (item.tariff_amount * item.qty_ordered || 0),
        0,
      ),
    );

    setSurchargeAmount(
      loadedOrderItems.reduce(
        (sum, item) => sum + (item.surcharge * item.qty_ordered || 0),
        0,
      ),
    );

    setTaxAmount(
      loadedOrderItems.reduce((sum, item) => sum + (item.tax || 0), 0),
    );
  }, [
    selectedReduxRate,
    loadedOrderItems,
    shippingInfo?.free_freight,
    shippingInfo?.preferred_rate,
  ]);

  return (
    <div className="mx-auto px-4 py-8">
      <RemainingTime /> {/* Order Number and Thank You Message */}
      <div className="flex flex-col md:flex-row justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Review Order
          </h1>
          <p className="text-gray-600">
            Please review your order carefully before submitting. If you need to
            make changes after submission, you will have to contact us.
          </p>
        </div>
      </div>
      {/* Main Content: Order Items on Left, Addresses on Right */}
      <div className="flex flex-col md:flex-row gap-8">
        {/* Order Items - Left Column */}
        <div className="md:w-3/5">
          {loadedOrderItems.map((item, index) => (
            <div
              key={index}
              className="flex flex-col md:flex-row items-start border-b border-gray-200 py-4"
            >
              <div className="w-24 h-24 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden mr-6 mb-4 md:mb-0">
                {item.product?.image_name ? (
                  <img
                    src={
                      process.env.REACT_APP_IMAGE_URL +
                      convertImageNameToWeb(item?.product?.image_name)
                    }
                    alt={item.description}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                    <img
                      src="/wide-track-smooth.png"
                      alt="Product"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>

              <div className="flex-grow">
                <h3 className="font-medium text-gray-900">
                  {item.product?.description || "21x7x15 WT SM GNM"}
                </h3>
                <p className="text-gray-700">
                  ${item.item_price?.toFixed(2)} x {item.qty_ordered}
                </p>
                <p className="text-gray-700">
                  ${item.tariff_amount?.toFixed(2)} x {item.qty_ordered} Tariff
                </p>
                <p className="text-gray-500 mt-1">
                  {item.product?.product_code || "04605002"}
                </p>
                {item.qty_backordered > 0 && (
                  <div className="flex flex-col gap-2 rounded-md bg-amber-50 border-l-4 border-amber-400 px-3 py-2 mt-2">
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-amber-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <p className="text-sm font-medium text-amber-800">
                        {item.qty_backordered} of {item.qty_ordered} will be
                        backordered
                      </p>
                    </div>
                    {item.alternative && (
                      <div className="flex items-center gap-2 pl-7">
                        <p className="text-sm text-amber-700">
                          {item.alternative.description} is in stock (
                          {item.alternative.available_qty} available) --
                          switch to avoid the backorder?
                        </p>
                        <button
                          type="button"
                          onClick={() => handleSwapToAlternative(item)}
                          disabled={
                            swappingProductCode === item.product?.product_code
                          }
                          className="rounded-md bg-[var(--primary-color)] px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-[var(--secondary-color)] disabled:opacity-50 whitespace-nowrap"
                        >
                          {swappingProductCode === item.product?.product_code
                            ? "Swapping..."
                            : "Swap Item"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div className="mt-4 md:mt-0 text-right">
                  <span className="font-medium">
                    $
                    {(
                      item.qty_ordered * item.item_price +
                      (item.tariff_amount || 0) * item.qty_ordered
                    ).toFixed(2)}
                  </span>
                </div>
                {item.tariff_amount > 0 && (
                  <div className="text-right">
                    <span className="text-sm text-gray-600">
                      (Tariff: $
                      {(item.qty_ordered * item.tariff_amount).toFixed(2)})
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Addresses - Right Column */}
        <div className="md:w-2/5">
          <div className="mb-3 font-bold text-gray-700 text-2xl">
            {customer?.name}
          </div>
          {/* Shipping Address */}
          <div className="flex gap-10">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Shipping Address
              </h3>
              <div className="text-gray-700">
                <p>{customer?.shipping_address?.street || ""}</p>
                {customer?.shipping_address?.street_two && (
                  <p>{customer.shipping_address.street_two}</p>
                )}
                <p>{customer?.shipping_address?.contact_name || ""}</p>
                <p>
                  {customer?.shipping_address?.city || ""}{" "}
                  {customer?.shipping_address?.state || ""}{" "}
                  {customer?.shipping_address?.zip_code || ""}
                </p>
                {customer?.shipping_address?.contact_phone && (
                  <p>Phone: {customer.shipping_address.contact_phone}</p>
                )}
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Billing Address
              </h3>
              <div className="text-gray-700">
                <p>{customer?.billing_address?.street}</p>
                {customer?.billing_address?.street_two && (
                  <p>{customer.billing_address.street_two}</p>
                )}
                <p>
                  {customer?.billing_address?.city || ""}, {""}
                  {customer?.billing_address?.state || ""}{" "}
                  {customer?.billing_address?.zip_code || ""}
                </p>
              </div>
            </div>
          </div>
          {/* Hold Date Picker */}
          <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-shrink-0">
                <label
                  htmlFor="holdDate"
                  className="block text-sm font-medium text-gray-900"
                >
                  Order Hold Date:
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Select when you want this order to be processed
                </p>
              </div>
              <div className="flex-grow max-w-xs">
                <input
                  type="date"
                  id="holdDate"
                  name="holdDate"
                  value={holdDate || getDefaultHoldDate()}
                  onChange={handleDateChange}
                  min={getDefaultHoldDate()}
                  max={getMaxDate()}
                  className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 text-sm shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                />
              </div>
              {/* <div className="text-xs text-gray-500">
                {new Date().getHours() >= ORDER_CUTOFF_HOUR ? (
                  <span className="text-amber-600">
                    ⚠️ Past cutoff time ({customerWarehouseCutoff?.cutoff}) -
                    earliest available: tomorrow
                  </span>
                ) : (
                  <span>
                    📅 Same-day processing available until{" "}
                    {customerWarehouseCutoff?.cutoff}
                  </span>
                )}
              </div> */}
            </div>
          </div>

          {/* Special Instructions */}
          <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex flex-col gap-3">
              <div>
                <label
                  htmlFor="specialInstructions"
                  className="block text-sm font-medium text-gray-900"
                >
                  Special Instructions
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Add any special handling or delivery instructions for this
                  order
                </p>
              </div>
              <div className="relative">
                <textarea
                  id="specialInstructions"
                  name="specialInstructions"
                  value={special_instructions || ""}
                  onChange={handleSpecialInstructionsChange}
                  maxLength={120}
                  rows={3}
                  placeholder="Enter special instructions (optional)..."
                  className="block w-full rounded-md border-gray-300 px-3 py-2 text-sm shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 focus:ring-inset resize-none"
                />
                <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                  {(special_instructions || "").length}/120
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary - Below Addresses */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Tariff</span>
              <span className="font-medium">${tariffAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Surcharges</span>
              <span className="font-medium">${surchargeAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Taxes</span>
              <span className="font-medium">${taxAmount.toFixed(2)}</span>
            </div>{" "}
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Shipping</span>
              <span className="font-medium">
                {shippingCost === 0
                  ? "FREE"
                  : selectedReduxRate?.type
                    ? `$${shippingCost.toFixed(2)}`
                    : "TBD"}
              </span>
            </div>
            {/* Residential fee note */}
            {address_classification ===
              ADDRESS_CLASSIFICATION.RESIDENTIAL.id && (
              <div className="text-sm text-gray-500 ml-4 -mt-1 mb-2">
                <div className="text-xs">*Residential fee will be added</div>
              </div>
            )}
            {/* Customer pickup note */}
            {selectedReduxRate?.type === "customer_pickup" && (
              <div className="text-sm text-gray-500 ml-4 -mt-1 mb-2">
                <div className="text-xs">
                  *Customer pick up has a 2 hr minimum wait time
                </div>
              </div>
            )}
            {selectedReduxRate?.isMultiWarehouse && (
              <div className="text-sm text-gray-500 ml-4 -mt-1 mb-2">
                <div>
                  Multiple warehouses ({selectedReduxRate.data.length}{" "}
                  locations)
                </div>
                <div className="text-xs">
                  Will be split into {selectedReduxRate.data.length} separate
                  orders
                </div>
              </div>
            )}
            <div className="flex justify-between py-4 border-t border-gray-200 mt-2">
              <span className="font-medium">Total</span>
              <span className="text-xl font-bold">
                $
                {(
                  subtotal +
                  tariffAmount +
                  shippingCost +
                  taxAmount +
                  surchargeAmount
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Multi-warehouse Notice */}
      {selectedReduxRate?.isMultiWarehouse && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Multiple Warehouse Order
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Your order will be fulfilled from{" "}
                  {selectedReduxRate.data.length} different warehouses. We will
                  automatically split this into {selectedReduxRate.data.length}{" "}
                  separate orders for you - one for each warehouse location.
                  You'll receive separate tracking information for each
                  shipment.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
